// ─────────────────────────────────────────────
//  native/platform.js — Capacitor platform guards (Phase 6)
//
//  Single source of truth for "are we running inside the native app?".
//  @capacitor/core is environment-agnostic: in a browser / jsdom test it
//  simply reports the "web" platform, so this file is safe to import
//  anywhere. Actual native PLUGIN packages must only ever be loaded via
//  dynamic import() behind these guards (D-10) so the web bundle and the
//  vitest suite never execute native code paths.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import { Capacitor } from "@capacitor/core";

// QA override: `localStorage["atc.forceNative"] = "1"` makes a BROWSER render
// the native mobile shell. Opt-in only, nothing sets it in code — default web
// behavior is byte-identical (the characterization suite still passes with
// storage empty). Exists because the two ways to see the mobile UI otherwise
// are a store build or an emulator, and the emulator path is blocked by
// OneDrive locking the Gradle build tree — this flag lets the /browse live
// walk and any dev tab exercise the real shell against the sandbox. Guarded:
// storage access throws in some WebViews/private modes, and a throw here at
// module-consumer time would be a boot-path failure.
function forcedNative() {
  try {
    if (typeof window === "undefined") return false;
    // URL switch so a PHONE can opt in without dev tools: ?forceNative=1
    // persists the flag (the URL param survives only one load; the stored
    // flag survives navigation), ?forceNative=0 clears it. Checked lazily on
    // each isNative() call — no module-scope side effect (boot-path rule).
    const qs = new URLSearchParams(window.location?.search || "");
    const q = qs.get("forceNative");
    if (q === "1") window.localStorage?.setItem("atc.forceNative", "1");
    else if (q === "0") window.localStorage?.removeItem("atc.forceNative");
    return window.localStorage?.getItem("atc.forceNative") === "1";
  } catch {
    return false;
  }
}

/** True when running inside the Capacitor native shell (android/ios). */
export function isNative() {
  return Capacitor.isNativePlatform() || forcedNative();
}

/** "android" | "ios" | "web" */
export function getPlatform() {
  return Capacitor.getPlatform();
}
